// Phase 8.1 + 11.1.5 + 12.24.5: 全局 fetch wrap + user auth + tier upgrade prompt

// 402 触发 — UI 监听这个 event 显示 upgrade modal
let _upgradeListeners = [];
export function onUpgradeRequired(fn) {
  _upgradeListeners.push(fn);
  return () => { _upgradeListeners = _upgradeListeners.filter(x => x !== fn); };
}
function _triggerUpgrade(body) {
  for (const fn of _upgradeListeners) {
    try { fn(body); } catch (e) { /* */ }
  }
}

// User access JWT is memory-only and short-lived. The rotating refresh token
// stays in an HttpOnly/Secure/SameSite cookie and is never exposed to JS.
const STORAGE_KEY = 'quant_api_token';
const USER_KEY = 'quant_user';

let _token = '';
try { if (typeof localStorage !== 'undefined') localStorage.removeItem(STORAGE_KEY); } catch {/* */}
let _user = null;
try {
  const cached = typeof localStorage !== 'undefined' ? localStorage.getItem(USER_KEY) : null;
  if (cached) _user = JSON.parse(cached);
} catch { _user = null; }

const _listeners = new Set();
const _userListeners = new Set();

export function getToken() { return _token; }
export function getUser() { return _user; }

// Phase 15: tier 等级 (0 free / 1 basic / 2 pro / 3 team). admin / system token = 最高 team.
const _TIER_MAP = { free: 0, basic: 1, pro: 2, team: 3 };

// 2026-07-03: 预览覆盖残留会让 admin 满屏「订阅解锁」且毫无提示(实锤user困惑)。
// 返回当前激活的预览档(仅当真实身份是 admin 时有效), 无则 null; Layout 用它挂显式提示牌。
export function tierPreviewActive() {
  const u = _user || {};
  if (!(u.role === 'admin' || u._is_system)) return null;   // 预览是 admin 调试功能, 其他人无效
  try {
    const ov = typeof localStorage !== 'undefined' ? localStorage.getItem('quant_tier_preview') : null;
    return ov && ov in _TIER_MAP ? ov : null;
  } catch { return null; }
}

export function clearTierPreview() {
  try { localStorage.removeItem('quant_tier_preview'); } catch {/* */}
}

export function tierRank() {
  // admin 预览覆盖: localStorage.setItem('quant_tier_preview','pro') 即可用 Pro 视角看页面 (basic/pro/team/free)
  const ov = tierPreviewActive();
  if (ov) return _TIER_MAP[ov];
  const u = _user || {};
  if (u.role === 'admin' || u._is_system) return 3;
  return _TIER_MAP[u.subscription_tier] ?? 0;
}

export function setToken(t) {
  _token = t || '';
}

export function setUser(u) {
  _user = u || null;
  if (typeof localStorage !== 'undefined') {
    if (_user) localStorage.setItem(USER_KEY, JSON.stringify(_user));
    else localStorage.removeItem(USER_KEY);
  }
  _userListeners.forEach(fn => { try { fn(_user); } catch {/* */} });
}

export function onUserChange(fn) {
  _userListeners.add(fn);
  return () => _userListeners.delete(fn);
}

export function onUnauthorized(fn) {
  _listeners.add(fn);
  return () => _listeners.delete(fn);
}

function _triggerUnauthorized(reason) {
  _listeners.forEach(fn => { try { fn(reason); } catch {/* */} });
}

const _nativeFetch = typeof window !== 'undefined' ? window.fetch.bind(window) : null;
let _refreshPromise = null;

function _cookie(name) {
  if (typeof document === 'undefined') return '';
  const prefix = `${name}=`;
  const row = document.cookie.split('; ').find(item => item.startsWith(prefix));
  return row ? decodeURIComponent(row.slice(prefix.length)) : '';
}

async function _refreshAccess() {
  if (!_nativeFetch) return false;
  if (_refreshPromise) return _refreshPromise;
  _refreshPromise = (async () => {
    try {
      const csrf = _cookie('csrf_refresh_token');
      const headers = csrf ? { 'X-CSRF-TOKEN': csrf } : {};
      const response = await _nativeFetch('/api/auth/refresh', {
        method: 'POST', credentials: 'same-origin', headers,
      });
      if (!response.ok) return false;
      const body = await response.json();
      setToken(body.access_token || '');
      if (body.user) setUser(body.user);
      return !!body.access_token;
    } catch {
      return false;
    } finally {
      _refreshPromise = null;
    }
  })();
  return _refreshPromise;
}

// === Wrap global fetch 一次 ===
if (typeof window !== 'undefined' && !window.__quantFetchWrapped) {
  const orig = _nativeFetch;
  window.fetch = async (input, init = {}) => {
    const headers = new Headers(init.headers || {});
    if (_token && !headers.has('Authorization')) {
      headers.set('Authorization', `Bearer ${_token}`);
    }
    const requestInit = { ...init, headers, credentials: init.credentials || 'same-origin' };
    let res = await orig(input, requestInit);
    if (res.status === 401) {
      const url = typeof input === 'string' ? input : (input?.url || '');
      if (!url.includes('/api/auth/')) {
        const refreshed = await _refreshAccess();
        if (refreshed) {
          const retryHeaders = new Headers(init.headers || {});
          retryHeaders.set('Authorization', `Bearer ${_token}`);
          res = await orig(input, { ...requestInit, headers: retryHeaders });
        } else {
          _triggerUnauthorized('401');
        }
      }
    }
    // Phase 12.24.5: 402 = Payment Required (tier 不够) → 弹 upgrade modal
    if (res.status === 402) {
      try {
        const cloned = res.clone();
        const body = await cloned.json();
        _triggerUpgrade(body);
      } catch (e) { /* */ }
    }
    return res;
  };
  window.__quantFetchWrapped = true;
}

// 啟動時驗一次當前 token 是否有效；順便載入 user info
export async function verifyToken() {
  try {
    if (!_token) await _refreshAccess();
    const r = await fetch('/api/auth/check');
    const body = await r.json();
    if (!body.enabled) return { enabled: false, ok: true, isSystem: false, userId: null };
    if (body.ok) {
      // 若是 user JWT，順便拉 me
      if (body.user_id) {
        try {
          const meRes = await fetch('/api/auth/me');
          if (meRes.ok) {
            const meBody = await meRes.json();
            if (meBody.user) setUser(meBody.user);
          }
        } catch {/* */}
      } else if (body.is_system) {
        // system token = admin 後門，無 user 物件
        setUser({ id: 1, email: 'admin (system token)', role: 'admin', subscription_tier: 'team', _is_system: true });
      }
      return { enabled: true, ok: true, isSystem: body.is_system, userId: body.user_id };
    }
    return { enabled: true, ok: false, isSystem: false, userId: null };
  } catch {
    return { enabled: true, ok: false, isSystem: false, userId: null };
  }
}

// === Phase 11.1.5: email + password 登入 / 註冊 ===

export async function loginWithPassword(email, password) {
  const res = await fetch('/api/auth/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });
  const body = await res.json();
  if (!res.ok) return { ok: false, error: body.error || `HTTP ${res.status}` };
  setToken(body.access_token);
  setUser(body.user);
  return { ok: true, user: body.user };
}

export async function registerWithPassword(email, password, captchaToken) {
  const res = await fetch('/api/auth/register', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password, captcha_token: captchaToken || '' }),
  });
  const body = await res.json();
  if (!res.ok) return { ok: false, error: body.error || `HTTP ${res.status}` };
  setToken(body.access_token);
  setUser(body.user);
  return { ok: true, user: body.user };
}

export async function loginWithGoogle(credential) {
  const res = await fetch('/api/auth/google', {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ credential }),
  });
  const body = await res.json();
  if (!res.ok) return { ok: false, error: body.error || `HTTP ${res.status}` };
  setToken(body.access_token);
  setUser(body.user);
  return { ok: true, user: body.user };
}

export function logout() {
  const csrf = _cookie('csrf_refresh_token');
  if (_nativeFetch) {
    _nativeFetch('/api/auth/logout', {
      method: 'POST', credentials: 'same-origin',
      headers: csrf ? { 'X-CSRF-TOKEN': csrf } : {},
    }).catch(() => {});
  }
  setToken('');
  setUser(null);
  // 觸發 AuthGate 重新進 locked
  _triggerUnauthorized('logout');
}
